<template>
	<WaitDialog :isActive="loading" title="Loading..." />

	<v-container fluid>
		<!-- App Context Toggle -->
		<v-row>
			<v-col cols="12">
				<v-btn-toggle v-model="appContext" mandatory color="primary" density="comfortable">
					<v-btn value="POINT">POINT</v-btn>
					<v-btn value="Operation">Operation</v-btn>
				</v-btn-toggle>
			</v-col>
		</v-row>

		<!-- Menu Items Grid -->
		<v-row class="mt-2">
			<v-col cols="12">
				<v-card class="text-left">
					<v-data-table
						:headers="menuHeaders"
						:items="menuItems"
						:loading="loading"
						density="comfortable"
						class="elevation-0"
						no-data-text="No menu items found"
						items-per-page="25"
						hover
					>
						<template #top>
							<v-toolbar flat>
								<v-toolbar-title>Command Center Menu Items</v-toolbar-title>
								<v-divider class="mx-4" inset vertical />
								<v-chip class="mr-2">{{ menuItems.length }} items</v-chip>
								<m-btn color="primary" prepend-icon="mdi-plus" @click="openAddDialog" class="mr-2">Add</m-btn>
								<m-btn color="primary" prepend-icon="mdi-refresh" @click="loadMenuItems">Refresh</m-btn>
							</v-toolbar>
						</template>

						<!-- eslint-disable vue/valid-v-slot -->
						<template v-slot:item.menuPgm="{ item }">
							{{ item.menuPgm === 'M' ? 'Menu' : 'Program' }}
						</template>

						<template v-slot:item.actions="{ item }">
							<v-btn icon size="small" variant="text" color="primary" @click="openEditDialog(item)" title="Edit">
								<v-icon>mdi-pencil</v-icon>
							</v-btn>
							<v-btn icon size="small" variant="text" color="error" @click="confirmDelete(item)" title="Delete">
								<v-icon>mdi-trash-can-outline</v-icon>
							</v-btn>
						</template>
					</v-data-table>
				</v-card>
			</v-col>
		</v-row>

		<!-- Add/Edit Menu Item Dialog -->
		<v-dialog v-model="showMenuDialog" max-width="800" persistent>
			<v-card>
				<v-card-title>{{ editingItem ? 'Edit Menu Item' : 'Add Menu Item' }}</v-card-title>
				<v-card-text>
					<v-row>
						<v-col cols="12" md="6">
							<v-text-field v-model="menuForm.menuId" label="Menu ID *" variant="outlined" density="comfortable" maxlength="10" :disabled="!!editingItem" />
						</v-col>
						<v-col cols="12" md="3">
							<v-text-field v-model.number="menuForm.sequence" label="Sequence *" variant="outlined" density="comfortable" type="number" :min="0" :max="99" />
						</v-col>
						<v-col cols="12" md="3">
							<v-select v-model="menuForm.menuPgm" :items="menuTypes" item-title="text" item-value="value" label="Type *" variant="outlined" density="comfortable" />
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="12">
							<v-text-field v-model="menuForm.desc" label="Description *" variant="outlined" density="comfortable" maxlength="80" />
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="12">
							<v-text-field v-model="menuForm.url" label="URL *" variant="outlined" density="comfortable" maxlength="256" />
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="12">
							<v-textarea v-model="menuForm.helpText" label="Help Text" variant="outlined" density="comfortable" maxlength="256" rows="2" :counter="256" />
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="12" md="4">
							<v-text-field v-model="menuForm.pgmName" label="Program Name" variant="outlined" density="comfortable" maxlength="10" />
						</v-col>
						<v-col cols="12" md="2">
							<v-text-field v-model="menuForm.ldaSelect" label="LDA Select" variant="outlined" density="comfortable" maxlength="1" />
						</v-col>
						<v-col cols="12" md="3">
							<v-text-field v-model="menuForm.launch" label="Launch" variant="outlined" density="comfortable" maxlength="1" />
						</v-col>
						<v-col cols="12" md="3">
							<v-text-field v-model.number="menuForm.launchSeq" label="Launch Seq" variant="outlined" density="comfortable" type="number" :min="0" :max="99" />
						</v-col>
					</v-row>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<m-btn @click="showMenuDialog = false" text="Cancel" />
					<m-btn
						color="primary"
						@click="submitMenuItem"
						:text="editingItem ? 'Update' : 'Add'"
						:disabled="!menuForm.menuId || !menuForm.desc || !menuForm.url || !menuForm.menuPgm"
					/>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Delete Confirm Dialog -->
		<v-dialog v-model="showDeleteDialog" max-width="400">
			<v-card>
				<v-card-title>Confirm Delete</v-card-title>
				<v-card-text>
					Are you sure you want to delete menu item "{{ deletingItem?.menuId }}" (sequence {{ deletingItem?.sequence }})?
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<m-btn @click="showDeleteDialog = false" text="Cancel" />
					<m-btn color="error" @click="deleteMenuItem" text="Delete" />
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script lang="ts">
import { ref, watch, inject } from 'vue';
import { useTableMaintenanceApi } from '@/composables/UseTableMaintenanceApi';
import { useCurrentUser } from '@/composables/UseCurrentUser';
import type { MenuItem } from '@/models/TableMaintenanceViewModels';
import MBtn from '@/components/BaseButton.vue';
import WaitDialog from '@/components/WaitDialog.vue';
import { Tell } from '@/services/Tell';

export default {
	name: 'MenuManagement',
	components: { MBtn, WaitDialog },
	route: {
		path: '/SSAM/menus',
		meta: {
			requiresAuth: true,
			title: 'Command Center Menus',
		},
	},

	setup() {
		const api = useTableMaintenanceApi();
		const tell = inject('tell') as Tell;
		const { getCurrentUser } = useCurrentUser();

		// ── State ──────────────────────────────────────────────────────────
		const loading = ref(false);
		const appContext = ref('POINT');
		const menuItems = ref<MenuItem[]>([]);

		const showMenuDialog = ref(false);
		const editingItem = ref<MenuItem | null>(null);
		const menuForm = ref(emptyMenuForm());

		const showDeleteDialog = ref(false);
		const deletingItem = ref<MenuItem | null>(null);

		const menuTypes = [
			{ text: 'Menu', value: 'M' },
			{ text: 'Program', value: 'P' },
		];

		const menuHeaders = [
			{ title: 'Menu ID', key: 'menuId', sortable: true },
			{ title: 'Seq', key: 'sequence', sortable: true, width: 70 },
			{ title: 'Type', key: 'menuPgm', sortable: true, width: 90 },
			{ title: 'Description', key: 'desc', sortable: true },
			{ title: 'URL', key: 'url', sortable: true },
			{ title: 'Program', key: 'pgmName', sortable: true, width: 100 },
			{ title: 'Launch', key: 'launch', sortable: true, width: 70 },
			{ title: '', key: 'actions', sortable: false, width: 100, align: 'center' as const },
		];

		function emptyMenuForm() {
			return {
				menuId: '', sequence: 0, menuPgm: 'P', desc: '',
				helpText: '', url: '', pgmName: '',
				ldaSelect: '', launch: '', launchSeq: 0,
			};
		}

		// ── Data loading ───────────────────────────────────────────────────
		async function loadMenuItems() {
			loading.value = true;
			try {
				menuItems.value = await api.getMenuItems(appContext.value);
			} catch (e: unknown) {
				tell.error(`Error loading menu items: ${(e as Error).message}`);
			} finally {
				loading.value = false;
			}
		}

		// ── CRUD ───────────────────────────────────────────────────────────
		function openAddDialog() {
			editingItem.value = null;
			menuForm.value = emptyMenuForm();
			showMenuDialog.value = true;
		}

		function openEditDialog(item: MenuItem) {
			editingItem.value = item;
			menuForm.value = { ...item };
			showMenuDialog.value = true;
		}

		async function submitMenuItem() {
			loading.value = true;
			try {
				const request = {
					user: await getCurrentUser(), appContext: appContext.value,
					...menuForm.value,
				};
				const result = editingItem.value
					? await api.updateMenuItem(request)
					: await api.addMenuItem(request);

				if (result.success) {
					tell.success(result.message);
					showMenuDialog.value = false;
					await loadMenuItems();
				} else {
					tell.error(result.message);
				}
			} catch (e: unknown) {
				tell.error(`Error: ${(e as Error).message}`);
			} finally {
				loading.value = false;
			}
		}

		function confirmDelete(item: MenuItem) {
			deletingItem.value = item;
			showDeleteDialog.value = true;
		}

		async function deleteMenuItem() {
			if (!deletingItem.value) return;
			loading.value = true;
			showDeleteDialog.value = false;
			try {
				const result = await api.deleteMenuItem(
					deletingItem.value.menuId, deletingItem.value.sequence,
					appContext.value, await getCurrentUser());
				if (result.success) {
					tell.success(result.message);
					await loadMenuItems();
				} else {
					tell.error(result.message);
				}
			} catch (e: unknown) {
				tell.error(`Error deleting menu item: ${(e as Error).message}`);
			} finally {
				loading.value = false;
				deletingItem.value = null;
			}
		}

		// ── Watchers ───────────────────────────────────────────────────────
		watch(appContext, () => loadMenuItems(), { immediate: true });

		return {
			loading, appContext, menuItems,
			showMenuDialog, editingItem, menuForm,
			showDeleteDialog, deletingItem,
			menuTypes, menuHeaders,
			loadMenuItems, openAddDialog, openEditDialog,
			submitMenuItem, confirmDelete, deleteMenuItem,
		};
	},
};
</script>
